<template>
  <validation-observer ref="observer" v-slot="{ invalid }">
    <Dialog @close="$emit('close')">
      <template #title>
        <span class="text-base">期間設定</span>
      </template>

      <template #contents>
        <!-- 期間設定 (開始日 & 期間) -->
        <v-row class="mb-2 mx-2">
          <!-- 開始日 -->
          <v-col cols="7">
            <label class="text-primary text-sm">開始日<span class="ml-1 red--text">*</span></label>
            <v-menu v-model="showCalendar" transition="slide-x-reverse-transition">
              <template #activator="{ on }">
                <v-text-field
                  v-model="formattedFromDate"
                  readonly
                  v-on="on"
                  outlined
                  dense
                  hide-details
                />
              </template>
              <v-date-picker
                v-model="localPeriodFrom"
                :day-format="(date) => new Date(date).getDate()"
                :active-picker.sync="activePicker"
                min="1900-01-01"
                no-title
                scrollable
                @input="showCalendar = false"
                header-color="primary"
              />
            </v-menu>
          </v-col>

          <!-- 期間 (日数) -->
          <v-col cols="5">
            <label class="text-primary text-sm">期間 (日数)</label>
            <validation-provider rules="duration" v-slot="{ errors }">
              <v-text-field
                v-model.number="duration"
                type="number"
                min="1"
                max="100"
                outlined
                dense
                :error-messages="errors"
              />
            </validation-provider>
          </v-col>
        </v-row>

        <!-- 設定値 -->
        <div>
          <label class="text-primary text-sm">設定値</label>
          <p>{{ periodLabel }}</p>
        </div>

        <div class="flex justify-end space-x-4 mt-8">
          <v-btn @click="cancel">キャンセル</v-btn>
          <v-btn color="primary" @click="set" :disabled="invalid">設定</v-btn>
        </div>
      </template>
    </Dialog>
  </validation-observer>
</template>

<script>
import Dialog from "@/components/templates/Dialog";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import { Timestamp } from "firebase/firestore";
import moment from "moment";
import { extend } from "vee-validate";
import { mapState } from "vuex";
extend("duration", {
  validate(value) {
    return value <= 100;
  },
  message: "期間は100日以内で設定してください。",
});

export default {
  name: "MedicationPeriodSetting",
  components: {
    Dialog,
  },
  mixins: [errorHandlerMixin()],
  props: {
    period: {
      required: false,
      type: Object,
      default: () => ({ from: Timestamp.fromDate(new Date()), to: null }),
    },
  },
  data() {
    return {
      // 開始日
      localPeriodFrom: "",
      // 期間
      duration: null,
      // カレンダー
      activePicker: null,
      showCalendar: false,
    };
  },
  computed: {
    ...mapState("user", ["selfUser"]),

    // 表示用の開始日
    formattedFromDate() {
      return this.localPeriodFrom ? this.formatDate(this.localPeriodFrom) : "";
    },
    // 表示用の終了日（開始日と期間から算出）
    calculatedTo() {
      if (this.localPeriodFrom && this.duration) {
        return moment(this.localPeriodFrom)
          .add(this.duration - 1, "days")
          .format("YYYY-MM-DD");
      } else {
        return "";
      }
    },
    // 表示用の期間ラベル
    periodLabel() {
      // from が設定されていない場合は表示しない
      if (!this.localPeriodFrom) return "";
      // to が無い場合、from のみ表示
      const fromFormatted = moment(this.localPeriodFrom).format("Y年M月D日");
      if (!this.calculatedTo) {
        return `${fromFormatted} ~`;
      }
      // from ~ to
      const toFormatted = moment(this.calculatedTo).format("Y年M月D日");
      return `${fromFormatted} ~ ${toFormatted}`;
    },
  },
  methods: {
    // 初期化
    initializeLocalPeriod() {
      // from
      if (this.period?.from) {
        this.localPeriodFrom = moment(this.period.from.toDate()).format("YYYY-MM-DD");
      } else {
        this.localPeriodFrom = moment().format("YYYY-MM-DD");
      }
      // to
      if (this.period?.to) {
        this.duration =
          moment(moment(this.period.to.toDate()).format("YYYY-MM-DD")).diff(
            moment(this.localPeriodFrom),
            "days"
          ) + 1;
      } else {
        this.duration = null;
      }
    },
    // 日付フォーマット（"YYYY-DD-MM" → "YYYY年M月D日"）
    formatDate(date) {
      if (!date) return null;
      return moment(date).format("YYYY年M月D日");
    },
    // キャンセル
    cancel() {
      this.$emit("close");
    },
    // 設定
    set() {
      const retPeriod = { from: null, to: null };
      if (this.localPeriodFrom) {
        const from = new Date(`${this.localPeriodFrom}T00:00:00Z`);
        retPeriod.from = Timestamp.fromDate(from);
      }
      if (this.calculatedTo) {
        const to = new Date(`${this.calculatedTo}T00:00:00Z`);
        retPeriod.to = Timestamp.fromDate(to);
      }
      this.$emit("set", {
        ...retPeriod,
      });
    },
  },
  created() {
    this.initializeLocalPeriod();
  },
};
</script>
